import React, {useState, useEffect, useRef,
	useMemo}										from 'react';
import {useNavigate, useParams}						from 'react-router-dom';
import moment										from 'moment';
import {useReactTable, ColumnDef, getCoreRowModel,
	getFilteredRowModel, getSortedRowModel, getFacetedRowModel,
	getFacetedUniqueValues, getFacetedMinMaxValues} from '@tanstack/react-table';

import Button										from '../ressources/components/Button';
import Icon											from '../ressources/components/Icon';
import Load											from '../ressources/components/Load/Load';
import Tabs											from '../ressources/components/Tabs';
import Textarea										from '../ressources/components/Textarea';
import Badge										from '../ressources/components/Badge';
import ModalFilter									from '../ressources/components/ModalFilter';
import Section										from '../ressources/components/Section';
import Avatar										from '../ressources/components/Avatar';
import Input										from '../ressources/components/Input';
import Table										from '../ressources/components/Table';
import MultiInputContainer							from '../ressources/components/Input/MultiInputContainer';

import {useAppSelector, useAppDispatch}				from '../app/hooks';
import {closeSupportsApi, getSupportsApi, noticeSupportsApi, postSupportMessageApi}		from '../redux/support/support';
import {TabsValues}									from '../ressources/components/Tabs/interface';
import {Support, SupportMessage}					from '../redux/support/interface';
import {FilterChecked}								from '../ressources/components/Modal/interface';
import {Document} 									from '../redux/documents/interface';
import {BadgeColor}									from '../ressources/components/Badge/interface';
import {InputValues}								from '../ressources/components/Input/interface';
import {LanguageType}								from '../ressources/language/type';

import ft_cssVar									from '../ressources/functions/getCssVar';
import {ft_checkSupportFilters}						from '../ressources/functions/filters';
import {ft_formatFirstUpperCase, ft_formatUserName}	from '../ressources/functions/format';

import img_support									from '../ressources/images/support.png';
import img_filter									from '../ressources/images/icon/filter.svg';
import img_truck									from '../ressources/images/icon/truck.svg';
import img_computer									from '../ressources/images/icon/devices/computer.svg';
import img_phone									from '../ressources/images/icon/devices/phone-2.svg';
import img_tablet									from '../ressources/images/icon/devices/tablet-2.svg';
import img_boxColored								from '../ressources/images/icon/box-colored.svg';
import img_supportIcon								from '../ressources/images/icon/support.svg';
import img_computerColored from '../ressources/images/icon/devices/computer-colored.svg';
import img_send										from '../ressources/images/icon/send.svg';
import img_file										from '../ressources/images/icon/file.svg';
import img_download									from '../ressources/images/icon/download.svg';
import img_search									from '../ressources/images/icon/search.svg';
import img_upload									from '../ressources/images/icon/upload.svg';

import LANGUAGE										from '../ressources/language';
import * as API										from '../ressources/API';

import './Support.css';
import Modal from '../ressources/components/Modal';
import {ft_genNotification, ft_pushNotification} from '../ressources/functions/notification';
import {pushUpload, setUploadPercent} from '../redux/documents/documents';


let TIMER: ReturnType<typeof setTimeout>;

function SupportView() {
	const dispatch = useAppDispatch();
	const user = useAppSelector(state => state.user.data);
	const supportLoad = useAppSelector(state => state.support.isLoad);
	const supports = useAppSelector(state => state.support.data);
	const orders = useAppSelector(state => state.order.data);
	const devices = useAppSelector(state => state.devices.data);
	const mobileLines = useAppSelector(state => state.mobileLines.data);
	const users = useAppSelector(state => state.companyUsers.data);
	const lang = useAppSelector(state => state.settings.lang);
	let notificationId = useAppSelector(state => state.notifications.lastId);

	const [view, set_view] = useState('');

	const [editData, set_editData] = useState<Support | undefined>(undefined);
	const [menu, set_menu] = useState<TabsValues>('ticketTracking');
	const [modal, set_modal] = useState<'close' | null>(null);

	const [messages, set_messages] = useState<SupportMessage[]>([]);
	const [documents, set_documents] = useState<Document[]>([]);
	const [textareaMessage, set_textareaMessage] = useState('');
	const [load, set_load] = useState(false);

	const [filters, set_filters] = useState<FilterChecked[]>([]);
	const [filterModal, set_filterModal] = useState(false);
	const [search, set_search] = useState<InputValues>('');

	const [documentsUpload, set_documentsUpload] = useState<File[]>([]);

	const messagesEndRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();
	const params = useParams();
	const TEXTE = LANGUAGE(lang);

	const devicesWithLines = useMemo(() => [...devices, ...mobileLines], [devices, mobileLines]);

	const columns = useMemo<ColumnDef<Support>[]>(() => [
		{
			size:       40,
			id:         'image',
			cell:       (v) => {
				let icon = img_supportIcon;

				if (v.row.original.order)
					icon = img_truck;
				else if (v.row.original.device) {
					const device = devicesWithLines.find(find => find._id === v.row.original.device);

					if (device?.shopData?.type === 'phone' || device?.notValalaData?.deviceType === 'phone')
						icon = img_phone;
					else if (device?.shopData?.type === 'tablet' || device?.notValalaData?.deviceType === 'tablet')
						icon = img_tablet;
					else
						icon = img_computer;
				}

				return (
					<div className={'userCardImg'}>
						<Icon
							size={30}
							iconBorder
							icon={icon}
							borderSize={0}
							iconColor={ft_cssVar('primary-600')}
						/>
					</div>
				);
			}
		},
		{
			id:         'number',
			accessorFn: row => row.number,
			header:     TEXTE.support.number
		},
		{
			id:         'date',
			header:     TEXTE.global.date,
			accessorFn: row => row.data?.creationDate || new Date(),
			cell:       v => (v.row.original.data?.creationDate ? moment(v.row.original.data.creationDate).format('DD/MM/YY') : '')
		},
		{
			id:         'status',
			header:     TEXTE.global.status,
			accessorFn: row => TEXTE.support.status[row.status],
			cell:       v => <span className={`supportStatus ${v.row.original.status}`}>{TEXTE.support.status[v.row.original.status]}</span>
		},
		{
			id:         'devices',
			header:     TEXTE.support.lastMessage,
			accessorFn: row => moment(row.lastMessage) || new Date(),
			cell:       v => `${TEXTE.support.last} ${moment(v.row.original.lastMessage).toNow(true)}`
		},
		{
			id:     'creator',
			header: TEXTE.support.openBy,
			cell:   (v) => {
				const creator = users.find(f => f?._id === v.row.original.data.creator);

				if (!creator)
					return (<div><div className={'supportMessengerMessageValalaLogo'} style={{height: 30, width: 30, backgroundSize: 24}} />{TEXTE.support.valalaTeam}</div>);
				return (<div><Avatar size={30} user={creator} />{ft_formatUserName(creator)}</div>);
			},
			accessorFn: row => ft_formatUserName(users.find(f => f?._id === row.data.creator))
		}
	], [users]);

	const useData = useMemo(() =>
		supports.filter(userData => ft_checkSupportFilters(filters, userData))
	, [devicesWithLines, filters]);

	const table = useReactTable({
		columns,
		data:  useData,
		state: {
			globalFilter: search
		},
		getCoreRowModel:        getCoreRowModel(),
		getFilteredRowModel:    getFilteredRowModel(),
		getSortedRowModel:      getSortedRowModel(),
		getFacetedRowModel:     getFacetedRowModel(),
		getFacetedUniqueValues: getFacetedUniqueValues(),
		getFacetedMinMaxValues: getFacetedMinMaxValues()
	});

	useEffect(() => {
		dispatch(getSupportsApi(user));
	}, []);

	useEffect(() => {
		if (params.idSupport === 'new')
			return set_view(params.idSupport);
		const findSupport = supports.find(find => find?._id === params.idSupport);

		setOrderData(findSupport || undefined);
		set_view(findSupport ? 'edit' : '');
	}, [params.idSupport, orders, supports]);

	useEffect(() => {
		set_messages([]);
		set_documents([]);

		if (TIMER)
			clearInterval(TIMER);

		if (!editData || menu === 'ticketTracking')
			return;

		set_load(true);
		getMessages(true);
		TIMER = setInterval(() => getMessages(), 5000);
		return () => clearInterval(TIMER);
	}, [editData, menu]);

	useEffect(scrollToBottom, [editData, menu]);

	function getMessages(scroll?: boolean) {
		if (!editData)
			return;

		API.getSupportDocument(editData._id, user)
		.then((response) => {
			if (response?.data)
				return set_documents(response.data);
			return set_documents([]);
		});

		API.getSupportMessages(editData._id, user)
		.then((response) => {
			set_load(false);

			if (scroll)
				setTimeout(() => scrollToBottom(), 100);

			if (response?.data)
				return set_messages(response.data);
			return set_messages([]);
		});
	}

	function sendMessage() {
		if (!editData)
			return;

		if (textareaMessage) {
			dispatch(postSupportMessageApi({
				user,
				idSupport: editData._id,
				data:      {
					message: textareaMessage
				}
			}))
			.then((response) => {
				if (!response.payload)
					return;

				set_textareaMessage('');
				if (!documentsUpload.length)
					getMessages();
			});
		}

		if (documentsUpload.length) {
			documentsUpload.map((document, i) => {
				if (document.size > 2097152)
					return;

				const index = documentsUpload.length + i;
				const dispatchData = {index, dispatch, function: setUploadPercent};

				const uploadFileData = {
					percent:  0,
					filename: document.name,
					size:     document.size,
					id:       editData._id
				};

				dispatch(pushUpload(uploadFileData));

				API.uploadSupportDocument(editData._id, document, user, dispatchData)
				.then((responseUpload) => {
					if (!responseUpload || !responseUpload.status)
						return dispatch(setUploadPercent({index, percent: `error: ${responseUpload?.error || ''}`}));

					const notificationData = ft_genNotification(TEXTE, 'fileDownloadDone', img_upload, `${TEXTE.support.ticket} n°${editData.number}`);
					notificationId = ft_pushNotification(dispatch, notificationId, notificationData);
				});
			});

			set_documentsUpload([]);
			getMessages();
		}
	}

	function closeTicket() {
		if (load || !editData)
			return;

		set_load(true);

		dispatch(closeSupportsApi({
			user,
			idSupport: editData._id,
			data:      {message: ''}
		}))
		.then((response) => {
			set_load(false);
			set_modal(null);
			if (!response.payload)
				return;
			getMessages();
		});
	}

	function sendNotice(notice: 'good' | 'bad') {
		if (load || !editData)
			return;

		set_load(true);

		dispatch(noticeSupportsApi({
			user,
			idSupport: editData._id,
			data:      {notice}
		}))
		.then(() => set_load(false));
	}

	function scrollToBottom() {
		if (messagesEndRef.current)
			messagesEndRef.current.scrollIntoView();
	}

	function getSupportName(support: Support, link?: boolean): React.ReactElement {
		let name = <span>{`${support.number}`}</span>;

		if (support.order) {
			const order = orders.find(find => find._id === support.order);

			if (order)
				name = <span onClick={() => link && navigate(`/order/${support.order}`)}>{`${TEXTE.menu.order} n°${order.number}`}</span>;
		} else if (support.device) {
			const device = devicesWithLines.find(find => find._id === support.device);

			if (device)
				name = <span onClick={() => link && navigate(`/devices/${support.device}`)}>{device.shopData?.name || device.name}</span>;
		} else if (support.type === 'mdm') {
			name = <span>{`"${TEXTE.support.mdm}"`}</span>;
			link = false;
		} else {
			name = <span>{`"${TEXTE.global.other}"`}</span>;
			link = false;
		}

		return link ? <u style={{cursor: 'pointer'}}>{name}</u> : name;
	}

	function setOrderData(support: Support | undefined) {
		if (!support)
			return set_editData(undefined);

		set_editData(support);
		set_view('edit');
	}

	function renderSupportStatusButton(statusButton: keyof LanguageType['support']['status']) {
		return (
			<Button
				fullWidth
				size={'sm'}
				onClick={() => set_filters([{key: 'status', values: [statusButton]}])}
				type={filters.some(f => f.key === 'status' && f.values.includes(statusButton)) ? 'secondary' : 'secondary-gray'}
			>
				{TEXTE.support.status[statusButton]}
				<Badge size={'sm'} color={'gray'} text={supports.filter(f => f.status === statusButton).length} />
			</Button>
		);
	}

	function renderSupportList() {
		let filterCount = 0;

		filters.forEach(filter => filterCount += filter.values.length);

		return (
			<div className={'globalDataContainerItem flex column'}>
				<MultiInputContainer>
					<Button
						fullWidth
						size={'sm'}
						onClick={() => set_filters([])}
						type={filters.some(f => f.key === 'status') ? 'secondary-gray' : 'secondary'}
					>
						{TEXTE.global.all}
						<Badge size={'sm'} color={'gray'} text={supports.length} />
					</Button>
					{renderSupportStatusButton('replacement')}
					{renderSupportStatusButton('inProgress')}
					{renderSupportStatusButton('done')}
				</MultiInputContainer>
				<br />
				<MultiInputContainer style={{alignItems: 'center'}}>
					<Button onClick={() => navigate('/support/new')}>
						{TEXTE.support.openTicket}
					</Button>

					<Input
						fullWidth
						value={search}
						onChange={set_search}
						placeholder={TEXTE.global.search}
						before={<img src={img_search} alt={TEXTE.users.search} />}
					/>

					<Button
						size={'sm'}
						type={'tertiary-gray'}
						iconBefore={img_filter}
						onClick={() => set_filterModal(true)}
					>
						{TEXTE.global.filter}
						{filterCount > 0 && <Badge size={'sm'} color={'gray'} text={filterCount} />}
					</Button>
				</MultiInputContainer>

				<Table
					data={table}
					load={supportLoad && supports.length === 0}
					onClickRow={({original}) => navigate(`/support/${original?._id}`)}
				/>
			</div>
		);
	}

	function renderStepList() {
		const stepList = [TEXTE.support.openTicket, TEXTE.support.waitTreatment, TEXTE.support.inProgress, TEXTE.support.ticketClose];
		const render: React.ReactElement[] = [];
		let actual = 0;

		if (editData) {
			actual = 1;

			if (editData.status === 'inProgress' || editData.status === 'replacement')
				actual = 2;
			if (editData.status === 'done')
				actual = 4;
		}

		stepList.forEach((stepText, i) => {
			let color: BadgeColor | undefined;
			let badgeText = '';

			if (actual > i) {
				color = 'success';
				badgeText = TEXTE.order.end;
			} if (actual === i) {
				color = 'gray';
				badgeText = TEXTE.order.current;
			}

			render.push(
				<div className={`orderStepListItem ${color} ${editData && 'fullWidth'}`}>
					<div className={'orderStepListItemNumber'}>{i + 1}</div>
					<div className={'flex flex-column'}>
						<span className={'text-md weight-500'}>{stepText}</span>

						{(i === actual && actual === 0) &&
							<span className={'orderStepListItemInfoText'}>{TEXTE.support.openTicketText}</span>
						}
						{(i === actual && actual === 1) &&
							<span className={'orderStepListItemInfoText'}>{TEXTE.support.waitTreatmentText}</span>
						}
						{(i === actual && actual === 2) &&
							<span className={'orderStepListItemInfoText'}>{TEXTE.support.inProgressText}</span>
						}
					</div>

					{color &&
						<Badge
							size={'sm'}
							color={color}
							text={ft_formatFirstUpperCase(badgeText)}
							style={{
								backgroundColor: (color === 'success' || color === 'error') ? '#ffffff' : ft_cssVar(`${color}-100`)
							}}
						/>
					}
				</div>
			);
		});

		return (
			<div className={'orderDefaultViewStepContainer'}>
				<img src={img_support} alt={''} />
				<div className={'orderDefaultViewTitle'}>
					{TEXTE.support.selectOrOpenTicket}
				</div>

				{render}

				{!editData &&
					<div className={'globalDataContainerItem orderButtonContainer'}>
						<Button
							fullWidth
							size={'lg'}
							icon={img_supportIcon}
							onClick={() => navigate('/support/new')}
						>
							{TEXTE.support.openTicket}
						</Button>
					</div>
				}
			</div>
		);
	}

	function renderChat() {
		if (!editData)
			return <></>;

		const render: React.ReactElement[] = [];

		messages.forEach((message, index) => {
			let userName = '';
			let actual = false;

			if (message.isValala)
				userName = TEXTE.support.valalaSupport;
			else if (typeof message.user === 'object') {
				userName = `${message.user.firstname || ''} ${message.user.lastname || ''}`;
				actual = message.user._id === user?._id;
			} else if (message.user !== user?._id)
				userName = ft_formatUserName(users.find(find => find?._id === message.user));
			else
				actual = true;

			render.push(
				<div className={'supportMessengerMessageContainer'} >
					{message.isValala && <div className={'supportMessengerMessageValalaLogo'} />}
					{(!message.isValala && !actual) &&
						<Avatar
							size={40}
							style={{marginRight: 12}}
							user={users.find(find => find?._id === message.user)}
							lastname={typeof message.user === 'object' ? message.user.lastname : undefined}
							firstname={typeof message.user === 'object' ? message.user.firstname : undefined}
							forceImage={typeof message.user === 'object' ? message.user.profilePicture : undefined}
						/>
					}

					<div className={`supportMessengerMessage ${!actual && 'isValala'}`}>
						<div>
							<span className={'text-sm weight-500 gray-700'}>{!actual && userName}</span>
							<span className={'text-xs weight-400 gray-600'}>{moment(message.data.creationDate).calendar()}</span>
						</div>
						<div>
							<span className={'text-md weight-400'}>
								{message.message?.split('\n').map((item, i) => {
									if (item)
										return <span key={i}>{item}<br /></span>;
									return <br key={i} />;
								})}
							</span>
						</div>
					</div>
				</div>
			);

			const nextMessage = messages[index + 1];

			documents.filter((document) => {
				const docDate = moment(document.data?.creationDate);
				const messageDate = moment(message.data.creationDate);
				const nextMessageDate = nextMessage ? moment(nextMessage.data.creationDate) : null;

				return docDate.isSameOrAfter(messageDate) && (nextMessageDate ? docDate.isBefore(nextMessageDate) : true);
			}).forEach((document) => {
				const sameDate = moment(message.data.creationDate).calendar() === moment(document.data?.creationDate).calendar();

				render.push(
					<div
						data-sameDate={sameDate}
						className={'supportMessengerMessageContainer'}
					>
						{document.isValala && <div className={'supportMessengerMessageValalaLogo'} style={sameDate ? {opacity: 0} : {}} />}
						<div className={`supportMessengerMessage ${document.isValala && 'isValala'}`}>
							{sameDate ?
								<div />
								:
								<div>
									<span className={'text-sm weight-500 gray-700'}>{/*ft_getDocumentCreator(document, users)*/}</span>
									<span className={'text-xs weight-400 gray-600'}>{moment(document.data?.creationDate).calendar()}</span>
								</div>
							}
							<div style={{cursor: 'pointer'}} onClick={() => API.downloadSupportDocument(editData._id, document, user)}>
								<Icon icon={img_file} iconColor={document.isValala ? '#000' : '#fff'} />
								<span className={'text-md weight-400'}>
									{document.name}
								</span>
								<div className={'flex'} />
								<Icon icon={img_download} iconColor={document.isValala ? '#000' : '#fff'} />
							</div>
						</div>
					</div>
				);
			});
		});

		return (
			<div className={'supportMessengerContainer'}>
				<div className={'supportMessenger'}>
					{load ? <Load center /> : render}

					{(editData.status === 'done' && !editData.notice) &&
						<div className={'supportNotice'}>
							<span className={'text-lg weight-500'}>{TEXTE.support.notice}</span>
							<br />
							<br />

							<MultiInputContainer>
								<Button
									size={'sm'}
									type={'secondary-gray'}
									onClick={() => sendNotice('bad')}
								>
									{TEXTE.global.no}
								</Button>
								<Button
									size={'sm'}
									type={'secondary-gray'}
									onClick={() => sendNotice('good')}
								>
									{TEXTE.global.yes}
								</Button>
							</MultiInputContainer>
						</div>
					}

					<div ref={messagesEndRef} />
				</div>

				<div className={'supportMessengerDocuments'}>
					<div style={{width: 16}}>
						<Input
							type={'file'}
							fileInputText={TEXTE.support.documentsRules}
							placeholder={TEXTE.support.descriptionPlaceHolder}
							accept={'gif,.jpg,.jpeg,.tiff,.png,.webp,.bmp,.pdf'}
							onChangeFile={files => (files?.[0] ? set_documentsUpload([...documentsUpload, files[0]]) : null)}
						>
							<Icon
								icon={img_file}
								onClick={() => null}
								popup={'Ajouter un fichier'}
								iconColor={ft_cssVar('gray-600')}
							/>
						</Input>
					</div>
					<div style={{display: 'flex', flex: 1, flexWrap: 'wrap', gap: '12px'}}>
						{renderDocuments()}
					</div>
				</div>

				<div className={'supportMessengerInput'}>
					<Textarea
						value={textareaMessage}
						onChange={set_textareaMessage}
						placeholder={TEXTE.support.writeMessage}
					/>
					<Button
						icon={img_send}
						onClick={sendMessage}
						disabled={!textareaMessage && !documentsUpload.length}
					/>
				</div>
			</div>
		);
	}

	function renderDocuments() {
		const render: React.ReactElement[] = [];

		documentsUpload.forEach((file, i) => {
			const toBig = file.size > 2097152;

			render.push(
				<Badge
					key={i}
					color={toBig ? 'error' : 'gray'}
					text={`${toBig ? `${TEXTE.global.fileToBig} - ` : ''}${file.name}`}
					onClose={() => {
						if (!documents)
							return;
						const copy: File[] = [...documentsUpload];

						copy.splice(i, 1);
						set_documentsUpload(copy);
					}}
				/>
			);
		});

		return render;
	}

	function renderDefaultView() {
		let icon;

		if (editData?.order)
			icon = img_boxColored;
		else if (editData?.device)
			icon = img_computerColored;

		return (
			<div className={'globalDataContainer'}>
				{editData &&
					<Section sticky className={'globalDataContainerItem column'}>
						<div className={'flex flex-center'}>
							{icon && <Icon icon={icon} iconSize={48} />}
							<span className={'text-lg weight-600'}>{`${TEXTE.support.ticketOn} `}{getSupportName(editData, true)}</span>
							<div className={'flex'} />
							<span className={'text-sm gray-500 weight-400'}>{`N°${editData.number}`}</span>
						</div>

						<div className={'flex flex-center'}>
							<Tabs

								alignLeft
								data={[
									{value: 'ticketTracking', render: TEXTE.support.ticketTracking},
									{
										value:  'discussionFollow',
										render: (
											<div>
												{TEXTE.support.discussionFollow}
												{moment(editData.lastMessage).isAfter(moment(editData.data.lastRead[0]?.date)) && <Badge text={'1'} color={'gray'} size={'sm'} />}
											</div>
										)
									}
								]}
								actual={menu}
								onChange={set_menu}
							/>

							<div className={'flex'} />

							{(editData.status === 'new' || editData.status === 'inProgress') &&
								<Button
									size={'sm'}
									type={'danger-reverse'}
									onClick={() => set_modal('close')}
								>
									{TEXTE.support.closeTicket}
								</Button>
							}
						</div>
					</Section>
				}

				<div style={{overflow: 'auto'}} className={`${(!editData || menu === 'discussionFollow') && 'flex'} globalDataContainerItem column`}>
					{(!editData || menu === 'ticketTracking') && renderStepList()}
					{(editData && menu === 'discussionFollow') && renderChat()}
				</div>
			</div>
		);
	}

	return (
		<div className={'globalMainContainer'}>
			{!view ? renderSupportList() : renderDefaultView()}

			{filterModal &&
				<ModalFilter
					isSupport
					filters={filters}
					setFilters={set_filters}
					onClose={() => set_filterModal(false)}
				/>
			}

			{modal === 'close' &&
				<Modal
					confirmLoad={load}
					confirmButton={closeTicket}
					onClose={() => set_modal(null)}
					cancelButton={() => set_modal(null)}
					header={
						<Icon
							size={40}
							iconBorder
							iconSize={18}
							borderSize={8}
							type={'warning'}
							icon={img_supportIcon}
							iconColor={ft_cssVar('warning-600')}
						/>
					}
				>
					<span className={'text-lg weight-600 gray-900'}>{TEXTE.devices.modal.deleteDevice}</span>
					<span className={'text-sm gray-600'}>
						{TEXTE.support.closeConfirm}
					</span>
				</Modal>
			}
		</div>
	);
}

export default SupportView;
